import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { Menu, MenuItem, Tooltip } from '@mui/material';

import {
  Box,
  Check,
  ChevronDown,
  Copy,
  Crosshair,
  Download,
  Eye,
  Lightbulb,
  Minus,
  Pencil,
  Plus,
  Trash2,
  X,
} from 'lucide-react';

import { useAppDispatch, useAppSelector } from '../../../../hooks';
import { httpGET } from '../../../../services/http';
import {
  addLamp,
  downloadIesFile,
  removeLamp,
  removeLamps,
  selectLamp,
  setSelectedLayer,
  setTargetCameraPosition,
  undoLampAction,
  updateLampDistance,
  updateLampName,
  updateLampPosition,
  updateLampProductVariant,
} from '../../../../store/reducers/workspace.reducer.ts';
import { getUser } from '../../../../store/selectors/user.selector';
import { selectActiveVersion } from '../../../../store/selectors/workspace.selector.ts';
import { Lamp } from '../../../../types/lamp.ts';
import { fetchCatalogueProduct } from '../../../../utils/catalogueProductCache';
import { buildCatalogueUrl } from '../../../../utils/catalogueUrl';
import {
  isAutopositioningInProgress,
  isCalculationCompleted,
  isCalculationStarted,
} from '../../../../utils/jobStatus.ts';
import { isMarketMatchedVersion } from '../../../../utils/lampMarketMatch';
import { fromDisplayLength, getLengthUnitLabel, toDisplayLength } from '../../../../utils/unitSystem.ts';
import IesPreview3DModal from '../../../catalogue/IesPreview3DModal';
import { NumericInput } from '../../../common/NumericInput';
import { ProductVariantPicker, type ProductVariantSelection } from '../../scene/ProductVariantPicker.tsx';
import { MountingHeights } from '../MountingHeights';
import { PanelIconButton, PanelSearch, PanelSection } from '../PanelSection';
import { DISTANCE_TO_GROUND_NA, MAX_LAMPS, MAX_LUMINAIRE_HEIGHT_METERS, MIN_LUMINAIRE_HEIGHT_METERS } from '../types';
import { useWorkspaceActions } from '../useWorkspaceActions';

const INPUT =
  'w-12 h-[22px] min-w-0 bg-black text-white px-1 rounded text-[11px] text-center tabular-nums focus:outline-none focus:ring-1 focus:ring-lime-500';

const clampHeight = (meters: number) =>
  Math.min(Math.max(meters, MIN_LUMINAIRE_HEIGHT_METERS), MAX_LUMINAIRE_HEIGHT_METERS);

function TickBox({ state }: { state: 'checked' | 'mixed' | 'empty' }) {
  return (
    <span
      className={`flex h-[13px] w-[13px] items-center justify-center rounded-[3px] border transition-colors ${
        state === 'empty' ? 'border-gray-500 hover:border-gray-300' : 'border-lime-400 bg-lime-400'
      }`}
    >
      {state === 'checked' && <Check size={10} strokeWidth={3} className="text-black" />}
      {state === 'mixed' && <Minus size={10} strokeWidth={3} className="text-black" />}
    </span>
  );
}

/** Luminaires: heights for autoposition before any exist, then the list with coordinates. */
export function LuminairesSection() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { addLampManually } = useWorkspaceActions();
  const unitSystem = useSelector(getUser)!.settings.units;
  const lamps = useAppSelector((s) => s.workspace.lamps);
  const modelLayers = useAppSelector((s) => s.workspace.modelLayers);
  const lampSimilarityDetails = useAppSelector((s) => s.workspace.lampSimilarityDetails);
  const version = useAppSelector(selectActiveVersion);

  const canModify = !isCalculationStarted(version);
  const autopositioning = isAutopositioningInProgress(version);
  const calcCompleted = isCalculationCompleted(version);
  const marketMatched = isMarketMatchedVersion(version);
  const unit = getLengthUnitLabel(unitSystem);
  const canAdd = canModify && !autopositioning && lamps.length < MAX_LAMPS;

  const [search, setSearch] = useState('');
  const [editingLampId, setEditingLampId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [pickerLampId, setPickerLampId] = useState<string | null>(null);
  const [catalogueLabels, setCatalogueLabels] = useState<Record<string, string>>({});
  const [iesPreview, setIesPreview] = useState<{ isOpen: boolean; iesUrl?: string; lampName: string }>({
    isOpen: false,
    lampName: '',
  });
  const iesRequestRef = useRef(0);
  // Gmail-style ticks: a checkbox per row (Shift for a range), a select-all checkbox with a menu,
  // and an action bar while anything is ticked.
  const [checked, setChecked] = useState<Set<string>>(() => new Set());
  const [selectMenuAnchor, setSelectMenuAnchor] = useState<HTMLElement | null>(null);
  const [deletedCount, setDeletedCount] = useState<number | null>(null);
  const lastCheckedRef = useRef<string | null>(null);
  const lastUndo = useAppSelector((s) => s.workspace.lampHistory.undoStack.at(-1));
  const chlLayers = useMemo(() => modelLayers.filter((l) => l.canHoldLuminaire), [modelLayers]);
  const sortedLamps = useMemo(
    () => [...lamps].sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })),
    [lamps],
  );
  const visibleLamps = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return sortedLamps;
    return sortedLamps.filter((lamp) => {
      const product = lamp.productId != null ? catalogueLabels[`${lamp.productId}-${lamp.variantId}`] : undefined;
      return (lamp.name || '').toLowerCase().includes(q) || !!product?.toLowerCase().includes(q);
    });
  }, [sortedLamps, search, catalogueLabels]);
  const totalLumen = useMemo(() => {
    if (!calcCompleted) return null;
    const outputs = lamps
      .map((l) => l.lumenOutput)
      .filter((o): o is number => typeof o === 'number' && Number.isFinite(o));
    return outputs.length ? outputs.reduce((sum, o) => sum + o, 0) : null;
  }, [lamps, calcCompleted]);

  // Drop ticks of luminaires that are gone (undo, another tab) and all of them once the set-up locks.
  useEffect(() => {
    setChecked((prev) => {
      if (!canModify) return prev.size ? new Set() : prev;
      const ids = new Set(lamps.map((l) => l.id));
      const next = new Set([...prev].filter((id) => ids.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [lamps, canModify]);

  // "3 deleted · Undo" stays for a few seconds, and only while that deletion is still the last step.
  const canUndoDelete = deletedCount != null && lastUndo?.type === 'REMOVE_LAMPS';
  useEffect(() => {
    if (deletedCount == null) return;
    const timer = setTimeout(() => setDeletedCount(null), 10000);
    return () => clearTimeout(timer);
  }, [deletedCount]);

  const toggleChecked = (lamp: Lamp, range: boolean) => {
    setDeletedCount(null);
    // Work out the rows now: the anchor ref is overwritten below, before React runs the updater.
    const anchor = lastCheckedRef.current;
    const from = anchor ? visibleLamps.findIndex((l) => l.id === anchor) : -1;
    const to = visibleLamps.findIndex((l) => l.id === lamp.id);
    const rangeIds =
      range && from >= 0 && to >= 0
        ? visibleLamps.slice(Math.min(from, to), Math.max(from, to) + 1).map((l) => l.id)
        : null;
    setChecked((prev) => {
      const next = new Set(prev);
      if (rangeIds) rangeIds.forEach((id) => next.add(id));
      else if (next.has(lamp.id)) next.delete(lamp.id);
      else next.add(lamp.id);
      return next;
    });
    lastCheckedRef.current = lamp.id;
  };

  const checkedVisible = visibleLamps.filter((l) => checked.has(l.id)).length;
  const allVisibleChecked = visibleLamps.length > 0 && checkedVisible === visibleLamps.length;
  const notAboveLamps = visibleLamps.filter((l) => l.distanceToGround === DISTANCE_TO_GROUND_NA);

  const checkOnly = (toCheck: Lamp[]) => {
    setDeletedCount(null);
    setChecked(new Set(toCheck.map((l) => l.id)));
    lastCheckedRef.current = null;
    setSelectMenuAnchor(null);
  };

  const deleteChecked = () => {
    const ids = [...checked];
    dispatch(removeLamps(ids));
    setChecked(new Set());
    setDeletedCount(ids.length);
  };

  const selectedLampId = useMemo(() => lamps.find((l) => l.isSelected)?.id ?? null, [lamps]);
  useEffect(() => {
    if (!selectedLampId) return;
    const frame = requestAnimationFrame(() => {
      document
        .querySelector(`[data-panel-lamp-id="${CSS.escape(selectedLampId)}"]`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
    return () => cancelAnimationFrame(frame);
  }, [selectedLampId]);

  // Resolve "Product · Variant" labels for catalogue-assigned lamps.
  useEffect(() => {
    let cancelled = false;
    lamps
      .filter((l) => l.productId != null && l.variantId != null)
      .forEach((lamp) => {
        const key = `${lamp.productId}-${lamp.variantId}`;
        if (catalogueLabels[key]) return;
        void fetchCatalogueProduct(lamp.productId!).then((info) => {
          if (cancelled) return;
          const variantName = info?.variants?.find((v) => Number(v.id) === lamp.variantId)?.name;
          setCatalogueLabels((prev) => ({ ...prev, [key]: variantName ?? info?.name ?? `Product #${lamp.productId}` }));
        });
      });
    return () => {
      cancelled = true;
    };
  }, [lamps]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleLamp = (lamp: Lamp) => {
    if (lamp.isSelected) dispatch(selectLamp(''));
    else {
      dispatch(selectLamp(lamp.id));
      dispatch(setSelectedLayer(null));
    }
  };

  const openIesPreview = async (iesFileId: number, lampName: string) => {
    const requestId = ++iesRequestRef.current;
    setIesPreview({ isOpen: true, lampName, iesUrl: undefined });
    try {
      const { data } = await httpGET(`/files/${iesFileId}/presigned-url/download`);
      if (requestId === iesRequestRef.current) setIesPreview((prev) => ({ ...prev, iesUrl: data.url }));
    } catch {
      // The modal shows its error state when the URL never arrives.
    }
  };

  const setCoordinate = (lamp: Lamp, axis: 0 | 1 | 2, display: number) => {
    const meters = fromDisplayLength(display, unitSystem);
    if (isNaN(meters)) return;
    const position = [...lamp.position] as [number, number, number];
    position[axis] = meters;
    dispatch(updateLampPosition({ id: lamp.id, position }));
  };

  const setLampHeight = (lamp: Lamp, display: number) => {
    const meters = fromDisplayLength(display, unitSystem);
    if (isNaN(meters)) return;
    const clamped = clampHeight(meters);
    const position = [...lamp.position] as [number, number, number];
    position[2] += clamped - (lamp.distanceToGround || 0);
    dispatch(updateLampPosition({ id: lamp.id, position }));
    dispatch(updateLampDistance({ id: lamp.id, distance: clamped }));
  };

  const saveName = (lamp: Lamp) => {
    if (editingName.trim()) dispatch(updateLampName({ id: lamp.id, name: editingName.trim() }));
    setEditingLampId(null);
  };

  const heightsBlock = chlLayers.length > 0 && lamps.length === 0 && (
    <div className="px-4 py-3 border-b border-white/5">
      <MountingHeights />
    </div>
  );

  const renderLamp = (lamp: Lamp) => {
    const notAbove = lamp.distanceToGround === DISTANCE_TO_GROUND_NA;
    const isChecked = checked.has(lamp.id);
    const catalogueKey = `${lamp.productId}-${lamp.variantId}`;
    const catalogueLabel =
      lamp.productId != null
        ? (catalogueLabels[catalogueKey] ??
          lampSimilarityDetails.find((d) => d.lampId === lamp.productId)?.name ??
          `Product #${lamp.productId}`)
        : '';
    const lumen = calcCompleted && lamp.lumenOutput !== undefined ? Math.round(lamp.lumenOutput) : null;
    const cell = (value: number, onChange: (v: number) => void) => (
      <NumericInput
        value={value}
        decimals={2}
        step="0.1"
        readOnly={!canModify}
        onValueChange={onChange}
        onClick={(e) => e.stopPropagation()}
        className={`${INPUT} ${canModify ? '' : 'opacity-70 cursor-not-allowed'}`}
      />
    );

    return (
      <div
        key={lamp.id}
        data-panel-lamp-id={lamp.id}
        onClick={() => toggleLamp(lamp)}
        className={`scroll-mt-32 border-b border-white/[0.04] cursor-pointer ${
          lamp.isSelected
            ? 'bg-lime-400/15 ring-1 ring-inset ring-lime-400/50'
            : isChecked
              ? 'bg-lime-400/[0.08]'
              : 'hover:bg-white/[0.04]'
        }`}
      >
        {/* One compact row: name and X / Y / Z / H, so 15+ luminaires fit on a laptop screen. */}
        <div className="flex items-center gap-1.5 h-[30px] px-3">
          {canModify && (
            <button
              type="button"
              role="checkbox"
              aria-checked={isChecked}
              aria-label={lamp.name}
              onClick={(e) => {
                e.stopPropagation();
                toggleChecked(lamp, e.shiftKey);
              }}
              className="-m-1 p-1 shrink-0"
            >
              <TickBox state={isChecked ? 'checked' : 'empty'} />
            </button>
          )}
          <Tooltip
            title={notAbove ? t('workspace.lampNotAboveModel') : lumen !== null ? `${lumen.toLocaleString()} lm` : ''}
          >
            <Lightbulb
              size={13}
              className={`shrink-0 ${
                notAbove ? 'text-red-400 animate-pulse' : lamp.variantId != null ? 'text-lime-400' : 'text-yellow-400'
              }`}
            />
          </Tooltip>
          {editingLampId === lamp.id ? (
            <input
              autoFocus
              type="text"
              value={editingName}
              onChange={(e) => setEditingName(e.target.value)}
              onBlur={() => saveName(lamp)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') saveName(lamp);
                else if (e.key === 'Escape') setEditingLampId(null);
              }}
              onClick={(e) => e.stopPropagation()}
              className="flex-1 min-w-0 text-[12px] bg-black text-white border border-lime-500 rounded px-1 outline-none"
            />
          ) : (
            <span
              className="flex-1 min-w-0 truncate text-[12px] text-white"
              title={lamp.name}
              onDoubleClick={(e) => {
                if (!canModify) return;
                e.stopPropagation();
                setEditingLampId(lamp.id);
                setEditingName(lamp.name || '');
              }}
            >
              {lamp.name}
            </span>
          )}
          <div className="flex shrink-0 gap-1">
            {cell(toDisplayLength(lamp.position[0], unitSystem), (v) => setCoordinate(lamp, 0, v))}
            {cell(toDisplayLength(lamp.position[1], unitSystem), (v) => setCoordinate(lamp, 1, v))}
            {cell(toDisplayLength(lamp.position[2], unitSystem), (v) => setCoordinate(lamp, 2, v))}
            {notAbove ? (
              <span
                className="w-12 h-[22px] flex items-center justify-center rounded border border-orange-500 bg-black text-[11px] text-yellow-400"
                title={t('workspace.lampNotAboveModel')}
              >
                N/A
              </span>
            ) : (
              cell(toDisplayLength(lamp.distanceToGround ?? 0, unitSystem), (v) => setLampHeight(lamp, v))
            )}
          </div>
        </div>

        {/* The selected luminaire opens up: output, catalogue product and its actions. */}
        {lamp.isSelected && (
          <div
            className="flex flex-wrap items-center gap-x-2 gap-y-1 pl-[31px] pr-2 pb-1.5 text-[11px]"
            onClick={(e) => e.stopPropagation()}
          >
            {lumen !== null && <span className="text-lime-400 tabular-nums">{lumen.toLocaleString()} lm</span>}
            {lamp.variantId != null && lamp.productId != null && (
              <span className="flex min-w-0 items-center gap-1">
                <Box size={11} className="shrink-0 text-lime-400" />
                <a
                  href={buildCatalogueUrl({
                    productId: lamp.productId,
                    seriesId: lamp.seriesId,
                    manufacturerId: lamp.manufacturerId,
                    variantId: lamp.variantId,
                  })}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={catalogueLabel}
                  className="min-w-0 max-w-[140px] truncate text-lime-400 hover:underline"
                >
                  {catalogueLabel}
                </a>
                {canModify && (
                  <button
                    type="button"
                    onClick={() => setPickerLampId(lamp.id)}
                    title={t('workspace.changeProduct')}
                    aria-label={t('workspace.changeProduct')}
                    className="p-0.5 text-gray-400 hover:text-lime-400"
                  >
                    <Pencil size={11} />
                  </button>
                )}
              </span>
            )}
            {marketMatched && lamp.variantId == null && canModify && (
              <button
                type="button"
                onClick={() => setPickerLampId(lamp.id)}
                className="flex items-center gap-1 text-amber-300 hover:text-lime-400"
              >
                <Box size={11} />
                {t('workspace.assignProduct')}
              </button>
            )}
            <div className="ml-auto flex items-center">
              {calcCompleted && lamp.iesFileId && (
                <>
                  <PanelIconButton
                    label={t('workspace.downloadIesFile')}
                    onClick={() => dispatch(downloadIesFile(lamp.iesFileId!))}
                  >
                    <Download size={14} />
                  </PanelIconButton>
                  <PanelIconButton
                    label={t('workspace.preview3dIes')}
                    onClick={() => void openIesPreview(lamp.iesFileId!, lamp.name || '')}
                  >
                    <Eye size={14} />
                  </PanelIconButton>
                </>
              )}
              <PanelIconButton
                label={t('workspace.findLuminaire')}
                onClick={() => {
                  const [x, y, z] = lamp.position;
                  dispatch(setTargetCameraPosition({ position: [x, y, z + 20], lookAt: [x, y, z] }));
                }}
              >
                <Crosshair size={14} />
              </PanelIconButton>
              <PanelIconButton
                label={t('workspace.duplicateLuminaire')}
                disabled={!canAdd}
                onClick={() => {
                  const { id: _id, uuid: _uuid, isSelected: _selected, lumenOutput: _lumen, ...rest } = lamp;
                  dispatch(addLamp({ ...rest, uuid: crypto.randomUUID(), name: `${rest.name} (copy)` }));
                }}
              >
                <Copy size={14} />
              </PanelIconButton>
              <PanelIconButton
                label={t('workspace.deleteLuminaire')}
                disabled={!canModify}
                onClick={() => dispatch(removeLamp(lamp.id))}
              >
                <Trash2 size={14} />
              </PanelIconButton>
            </div>
          </div>
        )}
      </div>
    );
  };

  const pickerLamp = lamps.find((l) => l.id === pickerLampId);

  return (
    <PanelSection
      title={t('workspace.luminaires')}
      count={search ? `${visibleLamps.length}/${lamps.length}` : lamps.length}
      toolbar={
        lamps.length > 0 && (
          <>
            <PanelSearch value={search} onChange={setSearch} placeholder={t('workspace.panel.luminaires.search')} />
            <div
              className={`flex items-center gap-1.5 h-8 px-3 text-[10px] text-gray-400 border-t border-white/5 ${
                checked.size > 0 ? 'bg-lime-400/[0.06]' : ''
              }`}
            >
              {canModify && (
                <div className="flex shrink-0 items-center">
                  <button
                    type="button"
                    role="checkbox"
                    aria-checked={allVisibleChecked ? true : checkedVisible > 0 ? 'mixed' : false}
                    aria-label={t('workspace.panel.luminaires.selectAll')}
                    onClick={() => checkOnly(checkedVisible > 0 ? [] : visibleLamps)}
                    className="-m-1 p-1"
                  >
                    <TickBox state={allVisibleChecked ? 'checked' : checkedVisible > 0 ? 'mixed' : 'empty'} />
                  </button>
                  <button
                    type="button"
                    aria-label={t('workspace.panel.luminaires.selectMenu')}
                    aria-haspopup="menu"
                    onClick={(e) => setSelectMenuAnchor(e.currentTarget)}
                    className="ml-0.5 h-5 w-4 flex items-center justify-center rounded text-gray-400 hover:text-white hover:bg-white/10"
                  >
                    <ChevronDown size={11} />
                  </button>
                </div>
              )}
              {checked.size > 0 ? (
                // Gmail-style: while anything is ticked, the header turns into the actions for it.
                <>
                  <span className="flex-1 min-w-0 truncate text-[11px] text-gray-200 tabular-nums">
                    {t('workspace.panel.luminaires.selectedCount', { count: checked.size })}
                  </span>
                  <PanelIconButton label={t('workspace.panel.luminaires.deleteSelected')} onClick={deleteChecked}>
                    <Trash2 size={14} />
                  </PanelIconButton>
                  <PanelIconButton label={t('workspace.panel.luminaires.clearSelection')} onClick={() => checkOnly([])}>
                    <X size={14} />
                  </PanelIconButton>
                </>
              ) : canUndoDelete ? (
                <span className="flex-1 min-w-0 truncate text-[11px] text-gray-200">
                  {t('workspace.panel.luminaires.deletedCount', { count: deletedCount })}
                  <button
                    type="button"
                    onClick={() => {
                      dispatch(undoLampAction());
                      setDeletedCount(null);
                    }}
                    className="ml-2 font-medium text-lime-400 hover:underline"
                  >
                    {t('workspace.panel.luminaires.undo')}
                  </button>
                </span>
              ) : (
                <>
                  {/* Lines up with the bulb column; with checkboxes the menu arrow takes that place. */}
                  {!canModify && <span className="w-[13px] shrink-0" />}
                  <span className="flex-1 min-w-0 truncate text-[11px]">{t('workspace.panel.luminaires.name')}</span>
                  <div className="flex shrink-0 gap-1" title={t('workspace.coordinatesInUnit', { unit })}>
                    {['X', 'Y', 'Z', 'H'].map((axis) => (
                      <span key={axis} className="w-12 text-center">
                        {axis} ({unit})
                      </span>
                    ))}
                  </div>
                </>
              )}
            </div>
            <Menu
              anchorEl={selectMenuAnchor}
              open={Boolean(selectMenuAnchor)}
              onClose={() => setSelectMenuAnchor(null)}
              slotProps={{
                paper: {
                  sx: {
                    backgroundColor: '#2a2a2a',
                    color: 'white',
                    border: '1px solid rgba(255,255,255,0.1)',
                    '& .MuiMenuItem-root': { fontSize: 12, minHeight: 30 },
                    '& .MuiMenuItem-root:hover': { backgroundColor: 'rgba(163,230,53,0.12)' },
                  },
                },
              }}
            >
              <MenuItem onClick={() => checkOnly(visibleLamps)}>{t('workspace.panel.luminaires.selectAll')}</MenuItem>
              <MenuItem onClick={() => checkOnly([])}>{t('workspace.panel.luminaires.selectNone')}</MenuItem>
              {notAboveLamps.length > 0 && (
                <MenuItem onClick={() => checkOnly(notAboveLamps)}>
                  {t('workspace.panel.luminaires.selectNotAbove', { count: notAboveLamps.length })}
                </MenuItem>
              )}
            </Menu>
          </>
        )
      }
      actions={
        <>
          {totalLumen !== null && (
            <span className="mr-1 text-[11px] text-gray-400 tabular-nums" title={t('workspace.totalLumenOutput')}>
              Σ <span className="text-lime-400">{Math.round(totalLumen).toLocaleString()} lm</span>
            </span>
          )}
          {canModify && (
            <PanelIconButton
              label={canAdd ? t('workspace.addLuminaireManually') : t('workspace.addLuminaireManuallyDisabled')}
              onClick={addLampManually}
              disabled={!canAdd}
            >
              <Plus size={16} />
            </PanelIconButton>
          )}
        </>
      }
    >
      {heightsBlock}

      {lamps.length === 0 ? (
        <p className="px-4 py-5 text-[13px] leading-relaxed text-gray-400">
          {chlLayers.length > 0 ? t('workspace.panel.luminaires.emptyWithChl') : t('workspace.panel.luminaires.empty')}
        </p>
      ) : (
        <div className="pb-3">
          {visibleLamps.length === 0 ? (
            <p className="px-4 py-5 text-[13px] text-gray-400">{t('workspace.panel.luminaires.noMatch')}</p>
          ) : (
            visibleLamps.map(renderLamp)
          )}
        </div>
      )}

      {pickerLampId != null && (
        <ProductVariantPicker
          initialSelection={
            pickerLamp?.productId != null &&
            pickerLamp.variantId != null &&
            pickerLamp.seriesId != null &&
            pickerLamp.manufacturerId != null
              ? {
                  manufacturerId: pickerLamp.manufacturerId,
                  seriesId: pickerLamp.seriesId,
                  productId: pickerLamp.productId,
                  variantId: pickerLamp.variantId,
                }
              : null
          }
          onSelect={(selection: ProductVariantSelection) => {
            dispatch(
              updateLampProductVariant({
                id: pickerLampId,
                productId: selection.productId,
                variantId: selection.variantId,
                seriesId: selection.seriesId,
                manufacturerId: selection.manufacturerId,
              }),
            );
            setPickerLampId(null);
          }}
          onClose={() => setPickerLampId(null)}
        />
      )}

      <IesPreview3DModal
        isOpen={iesPreview.isOpen}
        onClose={() => {
          iesRequestRef.current += 1;
          setIesPreview({ isOpen: false, lampName: '' });
        }}
        iesUrl={iesPreview.iesUrl}
        title={iesPreview.lampName}
      />
    </PanelSection>
  );
}
